import { type FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NewContractDrawer } from "web_myplann/Goals/Components/PinContract/NewContractDrawer";
import type { NewContractFormOutput } from "web_myplann/Goals/Forms/newContractFormSchema";
import { getNewContractEditPath } from "web_myplann/Goals/Helpers/getNewContractEditPath";
import { useCreateGoalContract } from "web_myplann/Goals/Hooks/useCreateGoalContract";
import { useGoalAdvisorChat } from "web_myplann/Goals/Hooks/useGoalAdvisorChat";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import type { NewContractType } from "web_myplann/Goals/Types/NewContractType";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type CreatedContract = { id: number; type: NewContractType };

type Props = {
	open: boolean;
	goal: GoalDetail;
	onClose: () => void;
	onPinCreated: (portfolioId: number, onSettled: () => void) => void;
};

export const NewContractFlow: FC<Props> = ({
	open,
	goal,
	onClose,
	onPinCreated,
}) => {
	const { t } = useResource();
	const navigate = useNavigate();
	const openChat = useGoalAdvisorChat(goal);
	const { createContract, isPending: isCreating } = useCreateGoalContract();

	const [created, setCreated] = useState<CreatedContract | null>(null);
	const [isPinning, setIsPinning] = useState(false);

	useEffect(() => {
		if (open) setCreated(null);
	}, [open]);

	const handleSubmit = (values: NewContractFormOutput) => {
		setIsPinning(true);
		createContract(values)
			.then((portfolioId) => {
				onPinCreated(portfolioId, () => {
					setIsPinning(false);
					setCreated({ id: portfolioId, type: values.type });
				});
			})
			.catch(() => setIsPinning(false));
	};

	const handleFillDetails = () => {
		if (!created) {
			onClose();
			return;
		}
		const editPath = getNewContractEditPath(created.type, created.id);
		onClose();
		navigate(editPath);
	};

	const handleMessageAdvisor = () => {
		onClose();
		openChat(
			t(Resources.Goals.Detail.Solution.Pin.New.Help.advisorMessage, {
				goal: goal.name,
			}),
		);
	};

	return (
		<NewContractDrawer
			open={open}
			created={created !== null}
			isPending={isCreating || isPinning}
			onClose={onClose}
			onSubmit={handleSubmit}
			onMessageAdvisor={handleMessageAdvisor}
			onFillDetails={handleFillDetails}
		/>
	);
};
